import type { FC } from "react";
import { useMemo, useState, useEffect } from "react";
import { TextInput, Select } from "flowbite-react";
import { HiOutlinePlus } from "react-icons/hi";
import {
  StandardForm,
  EntityFormDialog,
  CustomSelect,
  AddressSearchInput,
  AddressSearchResult,
  InfoMessageAtom,
} from "@components";
import { IDriver, ICompany, IVehicle, LicensesEnum } from "../../types";
import { useTranslation } from "react-i18next";
import { Driver } from "../../models";

export interface DriverModalProp {
  data: IDriver;
  companies: ICompany[];
  vehicles: IVehicle[];
  existingDrivers?: IDriver[];
  isShowing: boolean;
  onConfirm: (data: IDriver) => void | boolean | Promise<void | boolean>;
  onCancel: () => void;
  inline?: boolean;
  readOnly?: boolean;
  isSubmitting?: boolean;
  onDirty?: () => void;
}

export const DriverForm: FC<DriverModalProp> = function (
  props: DriverModalProp,
) {
  const defaultCompanyId =
    props.data.companyId.length > 0
      ? props.data.companyId
      : props.companies[0]?.id;

  const [selectedCompany, setSelectedCompany] = useState(defaultCompanyId);
  const availableVehicles = useMemo(
    () =>
      selectedCompany
        ? props.vehicles.filter(
            (vehicle) => vehicle.companyId === selectedCompany,
          )
        : [],
    [props.vehicles, selectedCompany],
  );
  // State for tracking selected licenses and vehicle
  const [selectedLicenses, setSelectedLicenses] = useState<string[]>(
    props.data.licenses || [],
  );
  const [allowedVehicleIds, setAllowedVehicleIds] = useState<string[]>(
    props.data.allowedVehicleIds ??
      (props.data.vehicleId ? [props.data.vehicleId] : []),
  );
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(
    props.data.vehicleId || null,
  );
  const [startAddress, setStartAddress] = useState(
    props.data.startAddress ?? "",
  );
  const [startZipCode, setStartZipCode] = useState(
    props.data.startZipCode ?? "",
  );
  const [startCity, setStartCity] = useState(props.data.startCity ?? "");
  const [showAddressValidationError, setShowAddressValidationError] =
    useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const [duplicateEmail, setDuplicateEmail] = useState(false);

  const { t } = useTranslation([
    "common",
    "user",
    "company",
    "inventory",
    "driver",
  ]);

  const isNew = Driver.isNew(props.data);

  const licenses = Object.values(LicensesEnum);
  const licenseOptions = licenses.map((l) => {
    return { value: l, label: l };
  });

  useEffect(() => {
    setSelectedCompany(defaultCompanyId);
  }, [defaultCompanyId]);

  useEffect(() => {
    setStartAddress(props.data.startAddress ?? "");
    setStartZipCode(props.data.startZipCode ?? "");
    setStartCity(props.data.startCity ?? "");
    setShowAddressValidationError(false);
    setSubmissionFailed(false);
    setDuplicateEmail(false);
  }, [props.data]);

  const handleAddressSelect = (address: AddressSearchResult) => {
    props.onDirty?.();
    setStartAddress(address.addressLine);
    setStartZipCode(address.postCode);
    setStartCity(address.city);
    setShowAddressValidationError(false);
  };

  // Custom handler for form submission
  const handleFormSubmit = async (formData: any) => {
    if (props.readOnly || props.isSubmitting) return;
    const normalizedEmail = String(formData.email || "")
      .trim()
      .toLowerCase();
    const originalEmail = (props.data.email || "").trim().toLowerCase();
    const emailChanged = normalizedEmail !== originalEmail;
    const hasDuplicate = (props.existingDrivers || []).some(
      (driver) =>
        driver !== props.data &&
        (driver.email || "").trim().toLowerCase() === normalizedEmail,
    );
    if ((isNew || emailChanged) && hasDuplicate) {
      setDuplicateEmail(true);
      setSubmissionFailed(false);
      return;
    }
    setDuplicateEmail(false);

    const hasAnyStartLocation = Boolean(
      startAddress.trim() || startZipCode.trim() || startCity.trim(),
    );
    const hasCompleteStartLocation = Boolean(
      startAddress.trim().length >= 5 &&
      startZipCode.trim().length >= 2 &&
      startCity.trim().length >= 2,
    );
    if (hasAnyStartLocation && !hasCompleteStartLocation) {
      setShowAddressValidationError(true);
      return;
    }

    // Add the selected licenses and vehicle to the form data
    const completeFormData: IDriver = {
      ...formData,
      licenses: selectedLicenses as LicensesEnum[],
      vehicleId: selectedVehicle || undefined,
      allowedVehicleIds: Array.from(
        new Set([
          ...allowedVehicleIds,
          ...(selectedVehicle ? [selectedVehicle] : []),
        ]),
      ),
      startAddress: startAddress.trim() || undefined,
      startZipCode: startZipCode.trim() || undefined,
      startCity: startCity.trim() || undefined,
    };

    // Call the original onConfirm function with the complete data
    setSubmissionFailed(false);
    const result = await props.onConfirm(completeFormData);
    if (result === false) setSubmissionFailed(true);
  };

  return (
    <fieldset
      disabled={props.readOnly || props.isSubmitting}
      onChange={props.onDirty}
      className="min-w-0"
    >
      <StandardForm
        showHeader={false}
        description={
          props.inline ? undefined : t("modals:descriptions.new_driver")
        }
        hideButtons={props.readOnly}
        isSubmitting={props.isSubmitting}
        fields={[
          {
            id: "companyId",
            label: t("company:select_company"),
            grid_style: "col-span-2",
            input: (
              <Select
                key={`driver-company-${defaultCompanyId}-${props.companies
                  .map((company) => company.id)
                  .join("-")}`}
                id="companyId"
                name="companyId"
                defaultValue={defaultCompanyId}
                onChange={(e) => setSelectedCompany(e.target.value)}
              >
                {props.companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </Select>
            ),
          },
          {
            id: "firstName",
            label: t("common:firstname"),
            input: (
              <TextInput
                id="firstName"
                name="firstName"
                placeholder={t("common:placeholder.firstname")}
                defaultValue={props.data.firstName ?? ""}
                type="text"
                required={true}
              />
            ),
          },
          {
            id: "lastName",
            label: t("common:lastname"),
            input: (
              <TextInput
                id="lastName"
                name="lastName"
                placeholder={t("common:placeholder.lastname")}
                defaultValue={props.data.lastName ?? ""}
                type="text"
                required={true}
              />
            ),
          },
          {
            id: "email",
            label: t("common:email"),
            input: (
              <TextInput
                id="email"
                name="email"
                placeholder={t("common:placeholder.email")}
                defaultValue={props.data.email ?? ""}
                required
              />
            ),
          },
          {
            id: "phoneNumber",
            label: t("common:phone"),
            input: (
              <TextInput
                id="phoneNumber"
                name="phoneNumber"
                placeholder={t("common:placeholder.phone")}
                defaultValue={props.data.phoneNumber ?? ""}
              />
            ),
          },
          {
            id: "licenses",
            label: t("user:driving_license"),
            grid_style: "col-span-2",
            input: (
              <CustomSelect
                type="checkbox"
                defaultValue={props.data.licenses || []}
                label={t("modals:placeholders.licenses")}
                items={licenseOptions.map((license) => ({
                  key: license.value,
                  label: license.label,
                  value: license.value,
                }))}
                onSelectChanged={(selected) => {
                  props.onDirty?.();
                  if (Array.isArray(selected)) {
                    setSelectedLicenses(selected);
                  }
                }}
              />
            ),
          },
          {
            id: "vehicleId",
            label: t("driver:form.default_vehicle"),
            grid_style: "col-span-2",
            input: (
              <CustomSelect
                type="radio"
                defaultValue={props.data.vehicleId || ""}
                label={t("driver:form.no_default_vehicle")}
                items={[
                  {
                    key: "",
                    value: "",
                    label: t("driver:form.no_default_vehicle"),
                  },
                  ...availableVehicles.map((vehicle) => ({
                    key: vehicle.id,
                    label: vehicle.registrationNumber,
                    value: vehicle.id,
                  })),
                ]}
                onSelectChanged={(selected) => {
                  props.onDirty?.();
                  if (typeof selected === "string") {
                    setSelectedVehicle(selected);
                  }
                }}
              />
            ),
          },
          {
            id: "allowedVehicleIds",
            label: t("driver:form.allowed_vehicles"),
            grid_style: "col-span-2",
            input: (
              <CustomSelect
                type="checkbox"
                defaultValue={allowedVehicleIds}
                label={t("driver:form.allowed_vehicles_hint")}
                items={availableVehicles.map((vehicle) => ({
                  key: vehicle.id,
                  label: vehicle.registrationNumber,
                  value: vehicle.id,
                }))}
                onSelectChanged={(selected) => {
                  props.onDirty?.();
                  if (Array.isArray(selected)) setAllowedVehicleIds(selected);
                }}
              />
            ),
          },
          {
            id: "startAddress",
            label: t("driver:form.start_location"),
            grid_style: "col-span-2",
            input: (
              <AddressSearchInput
                key={`driver-start-address-${props.data.id || props.data.email}`}
                id="driver-start-address"
                name="driver-start-address"
                placeholder={t("common:placeholder.address_line")}
                value={startAddress}
                onChange={(value) => {
                  setStartAddress(value);
                  if (!value.trim()) {
                    setStartZipCode("");
                    setStartCity("");
                  }
                }}
                onSelectAddress={handleAddressSelect}
                initiallyValidated={Boolean(props.data.startAddress)}
                showValidationError={showAddressValidationError}
              />
            ),
          },
          {
            id: "startZipCode",
            label: t("common:postcode"),
            input: (
              <TextInput
                id="startZipCode"
                name="startZipCode"
                placeholder={t("common:placeholder.postcode")}
                value={startZipCode}
                onChange={(event) => setStartZipCode(event.target.value)}
                autoComplete="new-password"
              />
            ),
          },
          {
            id: "startCity",
            label: t("common:city"),
            input: (
              <TextInput
                id="startCity"
                name="startCity"
                placeholder={t("common:placeholder.city")}
                value={startCity}
                onChange={(event) => setStartCity(event.target.value)}
                autoComplete="new-password"
              />
            ),
          },
        ]}
        onConfirm={handleFormSubmit}
        onCancel={props.onCancel}
        buttonConfirmText={
          props.inline ? t("common:save") : t("modals:button_texts.driver")
        }
        buttonConfirmPrependIcon={
          isNew ? <HiOutlinePlus className="mr-2 h-4 w-4" /> : null
        }
        buttonCloseText={t(props.inline ? "common:cancel" : "common:close")}
      />
      {submissionFailed && (
        <InfoMessageAtom variant="error" className="mt-4">
          {t("driver:form.save_failed")}
        </InfoMessageAtom>
      )}
      {duplicateEmail && (
        <InfoMessageAtom variant="error" className="mt-4">
          {t("driver:form.duplicate_email")}
        </InfoMessageAtom>
      )}
    </fieldset>
  );
};

export const DriverModal: FC<DriverModalProp> = function (
  props: DriverModalProp,
) {
  const { t } = useTranslation(["common", "user", "modals"]);

  return (
    <EntityFormDialog
      title={
        Driver.isNew(props.data)
          ? t("user:create_driver")
          : t("user:update_driver")
      }
      show={props.isShowing}
      onCancel={props.onCancel}
    >
      <DriverForm {...props} />
    </EntityFormDialog>
  );
};
